import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Table, Button, Input, notification, Modal, Select, DatePicker, Tag, Space, Alert } from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import { DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { hasPermission } from '../../components/permissions';
import '../../assets/styles/custom_style.css';
import moment from 'moment';

const { confirm } = Modal;
const { Option } = Select;

const createStyledTitle = (text) => (
  <span style={{ fontSize: '13px', color: '#7a0000', fontWeight: 700 }}>{text}</span>
);

function MessageLogList() {
  const [userList, setUserList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // ─── Multi-select ───────────────────────────────────────────────────────────
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [filters, setFilters] = useState({
    phone_no: '',
    message_type: '',
    send_status: '',
    timestamp: '',
  });

  useEffect(() => {
    getUserList();
  }, []);

  // auto-trigger on filter change (debounced)
  const debounce = (func, delay) => {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, arguments), delay);
    };
  };

  const debouncedGetUserList = useCallback(debounce((page, lim, sf, so, srch, flt) => {
    getUserList(page, lim, sf, so, srch, flt);
  }, 400), []);

  useEffect(() => {
    debouncedGetUserList(currentPage, limit, sortField, sortOrder, search, filters);
  }, [filters]);

  // ─── API ────────────────────────────────────────────────────────────────────
  async function getUserList(page = 1, lim = 10, sf = null, so = null, srch = '', flt = {}) {
    try {
      setLoading(true);
      setCurrentPage(page);
      setLimit(lim);
      const sortParam = sf && so ? `&sortField=${sf}&sortOrder=${so}` : '';
      const searchParam = srch ? `&search=${srch}` : '';
      const filterParams = Object.keys(flt).map(k => `${k}=${flt[k]}`).join('&');
      const response = await axios.get(
        `${baseUrl}/message-log/list?page=${page}&limit=${lim}${sortParam}${searchParam}&${filterParams}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );
      if (response.data.status) {
        setUserList(response.data.result);
        setPagination({ current: page, pageSize: lim, total: response.data.totalCount });
        setSelectedRowKeys([]); // clear selection on reload
      } else {
        notification.info({ message: 'Info', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message || 'Failed to load', placement: 'topRight' });
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    try {
      const response = await axios.delete(`${baseUrl}/message-log/delete/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.status === 200) {
        getUserList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
        notification.success({ message: 'Success', description: 'Log deleted successfully!', placement: 'topRight' });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: error.response?.data?.message || 'Delete failed', placement: 'topRight' });
    }
  }

  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Delete this log?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() { handleDelete(id); },
    });
  };

  // ─── Bulk delete ───────────────────────────────────────────────────────────
  const handleBulkDelete = () => {
    confirm({
      title: `Delete ${selectedRowKeys.length} selected log(s)?`,
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>You have selected <strong>{selectedRowKeys.length}</strong> log(s) to delete.</p>
          <p style={{ color: '#ff4d4f' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: `Yes, Delete ${selectedRowKeys.length} Logs`,
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await axios.post(
            `${baseUrl}/message-log/bulk-delete`,
            { ids: selectedRowKeys },
            { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
          );
          if (response.data.status) {
            notification.success({
              message: 'Deleted',
              description: response.data.message,
              placement: 'topRight',
            });
            setSelectedRowKeys([]);
            getUserList(1, limit, sortField, sortOrder, search, filters);
          }
        } catch (error) {
          notification.error({ message: 'Error', description: 'Bulk delete failed', placement: 'topRight' });
        }
      },
    });
  };

  // ─── Table handlers ─────────────────────────────────────────────────────────
  const handleTableChange = (pag, _flt, sorter) => {
    const { current, pageSize } = pag;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(formattedOrder);
    getUserList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleLimitChange = value => {
    setLimit(value);
    getUserList(1, value, sortField, sortOrder, search, filters);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    const reset = { phone_no: '', message_type: '', send_status: '', timestamp: '' };
    setFilters(reset);
    getUserList(1, limit, sortField, sortOrder, search, reset);
  };

  // ─── Row selection config ───────────────────────────────────────────────────
  const rowSelection = {
    selectedRowKeys,
    onChange: keys => setSelectedRowKeys(keys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  // ─── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: createStyledTitle("S.No"),
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 55,
      render: text => <span style={{ fontWeight: 600, fontSize: 12, color: '#7a0000' }}>{text}</span>,
    },
    {
      title: createStyledTitle("Phone"),
      dataIndex: "phone_no",
      key: "phone_no",
      fixed: 'left',
      width: 140,
    },
    {
      title: createStyledTitle("Message Type"),
      dataIndex: "message_type",
      key: "message_type",
      width: 200,
    },
    {
      title: createStyledTitle("Send Status"),
      dataIndex: "send_status",
      key: "send_status",
      width: 120,
    },
    {
      title: createStyledTitle("Date / Time"),
      dataIndex: "timestamp",
      key: "timestamp",
      width: 160,
    },
    {
      title: createStyledTitle("Response"),
      dataIndex: "response",
      key: "response",
    },
    {
      title: createStyledTitle("Action"),
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 70,
    },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Message Log</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              hasPermission('/message-log', 'deletePermission') && selectedRowKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={handleBulkDelete}
                  style={{ fontWeight: 600 }}
                >
                  Delete Selected ({selectedRowKeys.length})
                </Button>
              )
            }
          >
            <div className="table-responsive">
              <Table
                rowSelection={hasPermission('/message-log', 'deletePermission') ? rowSelection : undefined}
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                bordered
                title={() => (
                  <div>
                    {/* Bulk selection bar */}
                    {selectedRowKeys.length > 0 && (
                      <Alert
                        type="warning"
                        style={{ marginBottom: 10, padding: '6px 12px' }}
                        message={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>
                              <strong>{selectedRowKeys.length}</strong> log(s) selected
                            </span>
                            <Space>
                              <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={handleBulkDelete}
                                style={{ fontWeight: 600 }}
                              >
                                Delete Selected
                              </Button>
                              <Button
                                size="small"
                                onClick={() => setSelectedRowKeys([])}
                              >
                                Clear Selection
                              </Button>
                            </Space>
                          </div>
                        }
                      />
                    )}

                    {/* Filters */}
                    <Row gutter={[10, 10]} align="middle">
                      <Col xs={24} sm={12} md={5}>
                        <Input
                          size="small"
                          placeholder="Phone no"
                          value={filters.phone_no}
                          onChange={e => handleFilterChange('phone_no', e.target.value)}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Select
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Message Type"
                          value={filters.message_type || undefined}
                          onChange={v => handleFilterChange('message_type', v)}
                          allowClear
                        >
                          <Option value="Birthday SMS">Birthday SMS</Option>
                          <Option value="Suggestion on Birthday">Suggestion on Birthday</Option>
                          <Option value="Pending Payment Day">Pending Payment Day</Option>
                          <Option value="Payment Receipt">Payment Receipt</Option>
                          <Option value="Purnima Before Message">Purnima Before Message</Option>
                          <Option value="Purnima Same Day Message">Purnima Same Day Message</Option>
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <Select
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Send Status"
                          value={filters.send_status || undefined}
                          onChange={v => handleFilterChange('send_status', v)}
                          allowClear
                        >
                          <Option value="Success">Success</Option>
                          <Option value="Failure">Failure</Option>
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} md={4}>
                        <DatePicker
                          size="small"
                          style={{ width: '100%' }}
                          className="filter_dob"
                          format="DD-MM-YYYY"
                          value={filters.timestamp ? moment(filters.timestamp, 'DD-MM-YYYY') : null}
                          onChange={(_, ds) => handleFilterChange('timestamp', ds)}
                          placeholder="Date"
                        />
                      </Col>
                      <Col xs={24} sm={12} md={3}>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Button>
                      </Col>
                      <Col xs={24}>
                        <span style={{ fontSize: 12, color: '#666', marginRight: 8 }}>Per page:</span>
                        <Select size="small" value={limit} onChange={handleLimitChange} style={{ width: 70 }}>
                          {[10, 20, 30, 40, 50, 100].map(v => (
                            <Option key={v} value={v}>{v}</Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>
                  </div>
                )}

                dataSource={userList.map((user, index) => ({
                  key: user.log_id,         // use log_id as the row key for selection
                  displayKey: (limit * (currentPage - 1)) + index + 1,
                  phone_no: <span style={{ fontWeight: 500, fontSize: 13 }}>{user.phone_no || '—'}</span>,
                  message_type: <span style={{ fontSize: 12 }}>{user.message_type || '—'}</span>,
                  send_status: (
                    <Tag
                      color={user.send_status === 'Success' ? 'success' : 'error'}
                      style={{ fontWeight: 700, fontSize: 11 }}
                    >
                      {user.send_status || '—'}
                    </Tag>
                  ),
                  timestamp: (
                    <span style={{ fontSize: 12 }}>
                      {user.timestamp ? moment(user.timestamp).format('DD MMM YYYY, hh:mm A') : '—'}
                    </span>
                  ),
                  response: <span style={{ fontSize: 11, color: '#555' }}>{user.response || '—'}</span>,
                  action: (
                    <div className="button-container">
                      {hasPermission('/message-log', 'deletePermission') && (
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => showDeleteConfirm(user.log_id)}
                        />
                      )}
                    </div>
                  ),
                }))}

                pagination={{
                  ...pagination,
                  showTotal: total => `Total ${total} records`,
                  style: { marginRight: 20 },
                }}
                onChange={handleTableChange}
                className="ant-border-space"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default MessageLogList;
